/**
 * The perceptual hash the art-matching index is built from.
 *
 * A probe arrives as a grey `PHASH_SIZE` square matrix (sharp produces one in
 * the builder, and the Android plugin produces one from the camera frame), and
 * leaves as a fingerprint in which a single bit means "this low-frequency
 * coefficient was above the median".
 *
 * Why it is shaped this way:
 *
 *  - **Only low frequencies.** A 32x32 DCT, of which the 16x16 block nearest DC
 *    is taken: that is where the composition of a picture lives, and it is what
 *    survives recompression and a phone camera.
 *  - **DC is excluded and the rest is thresholded at the median.** A uniform
 *    brightness change moves only DC, so it cannot move a single bit; a
 *    contrast change scales every coefficient including the median, so it moves
 *    at most a few (measured: 4 bits, from integer rounding in the pixels).
 *    Both are asserted in the tests rather than assumed.
 *  - **128 bits, not 256.** An index entry is a hash plus the illustration it
 *    identifies, and the artifact ships on device: 52k artworks at 32 bytes is
 *    1.6 MiB, at 48 bytes it is 2.5 MiB. At this scale a 128-bit fingerprint
 *    still separates unrelated artwork by tens of bits (measured, see the
 *    builder's verification command), so the extra 128 bits would buy a
 *    collision margin nobody can observe and cost half a megabyte in every
 *    download. `phash_bits` and `phash_version` exist so this can be revised
 *    once, deliberately.
 */
import { dct2d } from './dct.js';
/**
 * Input edge length fed to the DCT.
 *
 * 64, not 32, and the choice is measured rather than aesthetic. What decides
 * how much a fingerprint moves under a re-framed or slightly rescaled probe is
 * not how many coefficients are kept but their frequency *relative to the
 * input*: coefficient `k` of an `N`-point DCT sits at `k / N`. Keeping 128
 * coefficients from a 32-point transform reaches out to `15 / 32`, nearly half
 * the way to Nyquist, and the tests measured 28-34 bits of movement under a
 * framing shift, a 6% zoom and camera noise - wider than the match threshold.
 * The same 128 coefficients of a 64-point transform reach only to `15 / 64`,
 * so the same fingerprint length becomes roughly half as sensitive while
 * unrelated artwork still separates by tens of bits.
 */
export const PHASH_SIZE = 64;
/** Bits per fingerprint. */
export const PHASH_BITS = 128;
/** Bytes per fingerprint. */
export const PHASH_BYTES = PHASH_BITS / 8;
/** Edge of the low-frequency block the coefficients are drawn from. */
export const PHASH_BLOCK = 16;
/** Bumped when the parameters or the algorithm change, so stored hashes can be reissued. */
export const PHASH_VERSION = 1;
/**
 * The coefficients a fingerprint is made of, in the order they are packed: the
 * lowest frequencies first (zigzag by `u + v`, ties broken by `u`), never DC.
 */
export function phashCoefficientOrder(block, bits) {
    if (!Number.isInteger(block) || block < 1)
        throw new Error(`block must be a positive integer, got ${block}`);
    if (!Number.isInteger(bits) || bits < 1)
        throw new Error(`bits must be a positive integer, got ${bits}`);
    if (bits > block * block - 1) {
        throw new Error(`asked for ${bits} bits but a ${block}x${block} block excluding DC has only ${block * block - 1} coefficients`);
    }
    const pairs = [];
    for (let u = 0; u < block; u += 1) {
        for (let v = 0; v < block; v += 1) {
            if (u === 0 && v === 0)
                continue; // DC is a brightness term, not structure.
            pairs.push({ u, v });
        }
    }
    pairs.sort((a, b) => a.u + a.v - (b.u + b.v) || a.u - b.u);
    return pairs.slice(0, bits);
}
/** Median of a copy, averaging the two middle values for an even count. */
function median(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = sorted.length >> 1;
    if (sorted.length % 2 === 1)
        return sorted[middle] ?? 0;
    return ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2;
}
/**
 * Hash a grey level array.
 *
 * Bits are packed most-significant-first into `bits / 8` bytes, which is the
 * order the art index stores them in.
 */
export function phashFromGrayLevels(levels, options = {}) {
    const size = options.size ?? PHASH_SIZE;
    const bits = options.bits ?? PHASH_BITS;
    const block = options.block ?? PHASH_BLOCK;
    if (levels.length !== size * size) {
        throw new Error(`phash expected ${size * size} grey levels for a ${size}x${size} image, got ${levels.length}`);
    }
    if (bits % 8 !== 0)
        throw new Error(`bits must be a multiple of 8, got ${bits}`);
    const coefficients = dct2d(levels, size);
    const order = phashCoefficientOrder(block, bits);
    const selected = order.map(({ u, v }) => coefficients[v * size + u] ?? 0);
    const threshold = median(selected);
    const hash = new Uint8Array(bits / 8);
    for (let index = 0; index < selected.length; index += 1) {
        if ((selected[index] ?? 0) <= threshold)
            continue;
        const byte = index >> 3;
        hash[byte] = (hash[byte] ?? 0) | (0x80 >> (index & 7));
    }
    return hash;
}
/** Bits differing between two fingerprints. */
export function hammingDistance(a, b) {
    if (a.length !== b.length) {
        throw new Error(`cannot compare hashes of different width: ${a.length * 8} and ${b.length * 8} bits`);
    }
    let distance = 0;
    for (let index = 0; index < a.length; index += 1) {
        let differing = (a[index] ?? 0) ^ (b[index] ?? 0);
        // Kernighan's count: one iteration per set bit.
        while (differing !== 0) {
            differing &= differing - 1;
            distance += 1;
        }
    }
    return distance;
}
export function hashToHex(hash) {
    let hex = '';
    for (const byte of hash)
        hex += byte.toString(16).padStart(2, '0');
    return hex;
}
export function hexToHash(hex, bits = PHASH_BITS) {
    const value = hex.trim().toLowerCase();
    if (value.length % 2 !== 0)
        throw new Error(`an even number of hex characters is required, got ${value.length}`);
    if (!/^[0-9a-f]*$/.test(value))
        throw new Error(`"${hex}" is not hex`);
    const hash = new Uint8Array(value.length / 2);
    for (let index = 0; index < hash.length; index += 1) {
        hash[index] = Number.parseInt(value.slice(index * 2, index * 2 + 2), 16);
    }
    if (hash.length * 8 !== bits) {
        throw new Error(`expected ${bits} bits (${bits / 8} bytes) but the value is ${hash.length * 8} bits`);
    }
    return hash;
}
/** 1 for identical fingerprints, 0 for fingerprints that share no bits. */
export function similarityFromDistance(distance, bits) {
    if (bits <= 0)
        return 0;
    const similarity = 1 - distance / bits;
    return similarity < 0 ? 0 : similarity > 1 ? 1 : similarity;
}
/**
 * How far apart two fingerprints may be and still be called the same artwork:
 * a fifth of the width.
 *
 * The tests assert that the perturbations a camera introduces move a fingerprint
 * by a handful of bits while unrelated artwork sits near the middle of the
 * space, and the builder's verification command reports the measured gap
 * against real crops. A fifth of the width is inside that gap with room to
 * spare.
 */
export function defaultThreshold(bits = PHASH_BITS) {
    return Math.round(bits * 0.2);
}
//# sourceMappingURL=phash.js.map